import { ClientApplication, FeatureKey, FeatureType, SubscriptionFeatureFlag } from '@numeo/types-core'
import { TMS_FEATURE_KEYS } from '@numeo/types-main-web-app'

export const isFeatureEnabled = (application: ClientApplication | null | undefined, feature: FeatureType) =>
    Boolean(application?.features?.find((f) => f?.feature === feature)?.enabled)

// Billing feature check - uses app-level env variable instead of backend feature flag
const isBillingEnabled = process.env.REACT_APP_BILLING_ENABLED === 'true'

/**
 * Existing/pre-billing users (no subscription object) are **permanently** exempt from every billing
 * gate — they predate billing and must never be blocked. Hardcoded `true` (NOT an env flag): the only
 * feature flag for TMS billing is the `tms-billing-enabled` Flipt flag. Users only become subject to
 * gating once they have a subscription (new signups), and only when that subscription lapses.
 */
const EXEMPT_PRE_BILLING_USERS = true

/**
 * Billing entitlement gate — mirrors the extension's useFeatureGate/isFeatureAllowed:
 * a feature is allowed unless the subscription explicitly disallows it. Fail-open when
 * billing is disabled, or (for pre-billing users) when there's no subscription.
 */
export const isEntitled = (application: ClientApplication | null | undefined, featureKey: FeatureKey): boolean => {
    if (!isBillingEnabled) return true
    const subscription = application?.subscription
    if (!subscription) return EXEMPT_PRE_BILLING_USERS
    return subscription.entitlements?.[featureKey]?.allowed !== false
}

/** Whether the org has the TMS addon active (featureFlags.tms, set by the TMS subscription per ADR-020). */
export const hasTmsAccess = (application: ClientApplication | null | undefined): boolean =>
    Boolean(application?.subscription?.featureFlags?.[SubscriptionFeatureFlag.TMS])

/** True for the TMS module entitlement keys — these are read from the SEPARATE tms.entitlements block. */
const TMS_KEY_SET = new Set<FeatureKey>(TMS_FEATURE_KEYS)
export const isTmsEntitlementKey = (key: FeatureKey): boolean => TMS_KEY_SET.has(key)

/**
 * TMS entitlement gate — reads the SEPARATE `subscription.tms.entitlements` block (ADR-020), never the
 * mixed base entitlements. Whether the gate is *enforced* at all is decided by the `tms-billing-enabled`
 * Flipt flag (checked in `FeatureGate`); this function only reports entitlement. Existing users (no
 * subscription) are permanently exempt; with a subscription but no active TMS the module is locked;
 * Basic excludes Fuel AI (allowed:false).
 */
export const isTmsEntitled = (application: ClientApplication | null | undefined, featureKey: FeatureKey): boolean => {
    const subscription = application?.subscription
    if (!subscription) return EXEMPT_PRE_BILLING_USERS
    if (!hasTmsAccess(application)) return false
    return subscription.tms?.entitlements?.[featureKey]?.allowed !== false
}
